// src/services/api/authService.ts
// Import only types from AWS SDK to prevent automatic initialization
import type {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  GlobalSignOutCommand,
  ChangePasswordCommand,
  GetUserCommand,
  UpdateUserAttributesCommand,
  RespondToAuthChallengeCommand
} from '@aws-sdk/client-cognito-identity-provider';

import { awsConfig, getAwsSdk } from '../config/aws-config';
import {
  AuthTokens,
  UserData,
  AuthError
} from '../../types/auth';
import { jwtDecode } from 'jwt-decode';  // Adjust import if needed

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Resolves a Cognito username from an email via the backend resolve-username endpoint.
 * The Cognito user pool does not have email as a username attribute, so this lookup
 * is required when users sign in with their email address.
 * Falls back to the original input if the lookup fails.
 */
async function resolveUsername(emailOrUsername: string): Promise<string> {
  if (!isEmail(emailOrUsername)) return emailOrUsername;

  try {
    const res = await fetch(`${awsConfig.apiEndpoint}/auth/resolve-username`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailOrUsername })
    });
    if (!res.ok) return emailOrUsername;
    const data = await res.json();
    return data.username || emailOrUsername;
  } catch {
    return emailOrUsername;
  }
}

// Custom error for new password challenge
export class NewPasswordRequiredError extends Error {
  session: string;
  challengeParameters: any;

  constructor(message: string, session: string, challengeParameters: any) {
    super(message);
    this.name = 'NewPasswordRequiredError';
    this.session = session;
    this.challengeParameters = challengeParameters;
  }
}

export class AuthService {
  private static client: CognitoIdentityProviderClient | null = null;

  // Initialize client lazily — no IAM credentials needed for USER_PASSWORD_AUTH
  private static async getClient() {
    if (!this.client) {
      const { CognitoIdentityProviderClient } = await getAwsSdk();
      this.client = new CognitoIdentityProviderClient({
        region: awsConfig.region
      });
    }
    return this.client;
  }

  // Debug method to check CSP configuration
  private static checkCSPConfiguration() {
    const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    // Check for multiple CSP tags
    const allCspTags = Array.from(document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]'));
  }

  // Debug method to test endpoint connectivity
  private static async testEndpointConnectivity() {
    const endpoint = 'https://cognito-idp.us-east-1.amazonaws.com/';
    try {
      await fetch(endpoint, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin
        }
      });
    } catch (error) {
      // Silently fail in production
    }
  }

  // Debug method to verify AWS configuration
  private static verifyAwsConfig() {
    // No logging in production
  }

  static async signIn(credentials: { username: string, password: string }): Promise<{ tokens: AuthTokens, user: UserData }> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    if (!awsConfig.userPoolWebClientId) {
      throw new Error('Cognito User Pool Client ID not configured');
    }

    // Resolve email → Cognito username if needed
    const resolvedUsername = await resolveUsername(credentials.username);

    // Helper function for retry logic with detailed logging
    const retryRequest = async <T>(fn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> => {
      try {
        const result = await fn();
        return result;
      } catch (error: any) {
        if (retries > 0 && (error.name === 'TypeError' || error.message === 'Failed to fetch')) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return retryRequest(fn, retries - 1);
        }
        throw error;
      }
    };

    try {
      const { InitiateAuthCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();

      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: awsConfig.userPoolWebClientId,
        AuthParameters: {
          USERNAME: resolvedUsername,
          PASSWORD: credentials.password
        }
      });

      // Add timeout to the request
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timeout after 10 seconds'));
        }, 10000);
      });

      // Execute the auth command with retry logic
      const authPromise = retryRequest(() => client.send(command));
      const response: any = await Promise.race([authPromise, timeoutPromise]);

      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        throw new NewPasswordRequiredError(
          'New password required',
          response.Session!,
          response.ChallengeParameters
        );
      }

      const authResult = response.AuthenticationResult;
      if (!authResult || !authResult.AccessToken || !authResult.IdToken) {
        throw new Error('Authentication failed: Invalid response from server');
      }

      const tokens: AuthTokens = {
        accessToken: authResult.AccessToken,
        idToken: authResult.IdToken,
        refreshToken: authResult.RefreshToken || '',
        expiresIn: authResult.ExpiresIn || 3600
      };

      const user = this.extractUserFromToken(authResult.IdToken);

      return { tokens, user };

    } catch (error: any) {
      if (error instanceof NewPasswordRequiredError) {
        throw error;
      }

      switch (error.name) {
        case 'TypeError':
          if (error.message === 'Failed to fetch') {
            throw new Error('Network error: Unable to connect to AWS Cognito. Please check your network connection and AWS configuration');
          }
          throw error;
        case 'NotAuthorizedException':
          throw new Error('Invalid username or password');
        case 'UserNotConfirmedException':
          throw new Error('User is not confirmed. Please check your email for verification link');
        case 'UserNotFoundException':
          throw new Error('User does not exist');
        case 'InvalidParameterException':
          throw new Error('Invalid parameters provided');
        case 'TooManyRequestsException':
          throw new Error('Too many login attempts. Please try again later');
        default:
          throw new Error(`[${error.name}] ${error.message}`);
      }
    }
  }

  static async completeNewPassword(
    user: any,
    newPassword: string
  ): Promise<any> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { RespondToAuthChallengeCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new RespondToAuthChallengeCommand({
        ClientId: awsConfig.userPoolWebClientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ChallengeResponses: {
          USERNAME: user.ChallengeParameters.USER_ID_FOR_SRP,
          NEW_PASSWORD: newPassword
        },
        Session: user.Session
      });

      const response = await client.send(command);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async forgotPassword(email: string): Promise<void> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { ForgotPasswordCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new ForgotPasswordCommand({
        ClientId: awsConfig.userPoolWebClientId,
        Username: email
      });

      const response = await client.send(command);
      return;
    } catch (error: any) {
      switch (error.name) {
        case 'UserNotFoundException':
          throw new Error('If an account exists, a code will be sent to your email');
        case 'LimitExceededException':
          throw new Error('Too many attempts. Please try again later');
        case 'InvalidParameterException':
          throw new Error('Please provide a valid email address');
        case 'NotAuthorizedException':
          throw new Error('Password reset is not allowed for this account');
        case 'TypeError':
          throw new Error('Network error: Unable to reach authentication service. Please check your connection.');
        default:
          throw new Error(`[${error.name}] ${error.message}`);
      }
    }
  }

  static async confirmForgotPassword(formdata: { email: string, code: string, newPassword: string }): Promise<void> {
    const { email, code, newPassword } = formdata;
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { ConfirmForgotPasswordCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new ConfirmForgotPasswordCommand({
        ClientId: awsConfig.userPoolWebClientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword
      });

      const response = await client.send(command);
      return;
    } catch (error: any) {
      switch (error.name) {
        case 'CodeMismatchException':
          throw new Error('Invalid verification code');
        case 'ExpiredCodeException':
          throw new Error('Verification code has expired');
        case 'InvalidPasswordException':
          throw new Error(
            'Password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters'
          );
        case 'LimitExceededException':
          throw new Error('Too many attempts. Please try again later');
        default:
          throw new Error(`[${error.name}] ${error.message}`);
      }
    }
  }

  static validatePassword(password: string): boolean {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  static async signOut(accessToken: string): Promise<void> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { GlobalSignOutCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new GlobalSignOutCommand({
        AccessToken: accessToken
      });
      await client.send(command);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  static async changePassword(accessToken: string, oldPassword: string, newPassword: string): Promise<void> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { ChangePasswordCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword
      });
      await client.send(command);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  static async updateUserAttributes(accessToken: string, attributes: Record<string, string>): Promise<void> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { UpdateUserAttributesCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const userAttributes = Object.entries(attributes).map(([Name, Value]) => ({
        Name,
        Value
      }));

      const command = new UpdateUserAttributesCommand({
        AccessToken: accessToken,
        UserAttributes: userAttributes
      });

      await client.send(command);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  static async getCurrentUser(accessToken: string): Promise<UserData> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { GetUserCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new GetUserCommand({
        AccessToken: accessToken
      });

      const response = await client.send(command);

      if (!response.UserAttributes) {
        throw this.createError('UserError', 'Failed to get user attributes');
      }

      const attributes = response.UserAttributes.reduce((acc, attr) => {
        if (attr.Name && attr.Value) {
          acc[attr.Name] = attr.Value;
        }
        return acc;
      }, {} as Record<string, string>);

      return {
        id: attributes.sub,
        email: attributes.email,
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        verified: attributes.email_verified === 'true',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  static async refreshTokens(refreshToken: string): Promise<AuthTokens> {
    try {
      // Skip if configuration is invalid
      if (!awsConfig.isConfigValid) {
        throw new Error('AWS configuration is invalid');
      }
      
      // Dynamically import the needed command
      const { InitiateAuthCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const client = await this.getClient();
      
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: awsConfig.userPoolWebClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken
        }
      });

      const response = await client.send(command);
      const authResult = response.AuthenticationResult;

      if (!authResult?.AccessToken || !authResult.IdToken) {
        throw this.createError('TokenRefreshError', 'Failed to refresh tokens');
      }

      return {
        accessToken: authResult.AccessToken,
        idToken: authResult.IdToken,
        refreshToken: refreshToken,
        expiresIn: authResult.ExpiresIn || 3600
      };
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  static isTokenValid(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp > currentTime;
    } catch {
      return false;
    }
  }

  private static extractUserFromToken(idToken: string): UserData {
    try {
      const decoded: any = jwtDecode(idToken);
      return {
        id: decoded.sub,
        email: decoded.email,
        firstName: decoded['given_name'],
        lastName: decoded['family_name'],
        verified: decoded.email_verified === true || decoded.email_verified === 'true',
        createdAt: new Date(decoded.auth_time * 1000).toISOString(),
        updatedAt: new Date(decoded.auth_time * 1000).toISOString()
      };
    } catch (error) {
      throw this.createError('TokenDecodeError', 'Failed to decode user data from token');
    }
  }

  private static handleError(error: any): AuthError {
    if (error.name === 'NotAuthorizedException') {
      return this.createError('InvalidCredentials', 'Invalid email or password');
    }
    if (error.name === 'UserNotConfirmedException') {
      return this.createError('UserNotConfirmed', 'Please verify your email address');
    }
    if (error.name === 'UsernameExistsException') {
      return this.createError('EmailExists', 'An account with this email already exists');
    }
    if (error.name === 'CodeMismatchException') {
      return this.createError('InvalidCode', 'Invalid verification code');
    }
    if (error.name === 'ExpiredCodeException') {
      return this.createError('ExpiredCode', 'Verification code has expired');
    }
    if (error.name === 'LimitExceededException') {
      return this.createError('TooManyAttempts', 'Too many attempts. Please try again later');
    }

    return this.createError(
      error.name || 'UnknownError',
      error.message || 'An unknown error occurred'
    );
  }

  private static createError(code: string, message: string): AuthError {
    return { code, message };
  }
}

// # Test the API with the token
// curl -v -X GET \
//   "https://dlpe8931eb.execute-api.us-east-1.amazonaws.com/Stage/admin/donors" \
//   -H "Authorization: Bearer $(cat token.txt)" \
//   -H "Content-Type: application/json" \
//   -H "Origin: http://hbi-website.netlify.app"
